import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

// Realtime da PÁGINA PÚBLICA (/loja/<slug>): mudança que o lojista salva no
// painel aparece na tela aberta sem F5. Versão anônima do use-orders-realtime:
// sem etapa de token (o canal só transporta "algo mudou" — o conteúdo continua
// vindo da query pública de catálogo).
//
// Rajada: debounce COM CAUDA — sino dentro da janela ADIA o refetch, nunca
// descarta; o último sempre dispara e a tela termina no estado final.
//
// Rede de segurança quando o WS falha: reconexão com backoff + refetch ao
// reconectar (cobre o vão), e enquanto DESCONECTADO um backstop de 60s liga —
// a página degrada pra polling lento em vez de congelar no passado. Conectado,
// o backstop fica desligado (zero polling em operação normal).

const DEBOUNCE_MS = 300;
const RECONNECT_MIN_MS = 2_000;
const RECONNECT_MAX_MS = 30_000;
const PING_MS = 25_000;
const OFFLINE_BACKSTOP_MS = 60_000;
// Pulso lento: teto UNIVERSAL de atraso, agnóstico ao estado do socket.
// Cobre o que as outras camadas não veem — NOTIFY perdido sem sino posterior
// e o WS meio-aberto (TCP vivo aqui, morto lá: o reconnect nunca dispara
// porque o socket parece saudável). Três guardas o deixam ~grátis: só com a
// aba visível (oculta, o refetch-ao-focar cura na volta), inerte se algo
// buscou há menos de 5min (sino saudável = pulso nunca dispara), e sem olhar
// o socket (é o ponto: cobre inclusive modos de falha ainda não imaginados).
const PULSE_MS = 5 * 60_000;
const PULSE_CHECK_MS = 30_000;

export function useCatalogRealtime(slug: string | undefined, onPing?: () => void): void {
  const queryClient = useQueryClient();
  const onPingRef = useRef(onPing);
  onPingRef.current = onPing;

  useEffect(() => {
    if (!slug) return;

    let ws: WebSocket | null = null;
    let stopped = false;
    let reconnectMs = RECONNECT_MIN_MS;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let pingTimer: ReturnType<typeof setInterval> | null = null;
    let offlineTimer: ReturnType<typeof setInterval> | null = null;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    let debounceAgain = false;
    let lastFetchAt = Date.now();

    const refetchCatalog = () => {
      lastFetchAt = Date.now();
      // Prefixo pega Storefront (["catalog", slug, unitId]) e Checkout juntos.
      void queryClient.invalidateQueries({ queryKey: ["catalog", slug] });
      onPingRef.current?.();
    };

    const pulseTimer = setInterval(() => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      if (Date.now() - lastFetchAt >= PULSE_MS) refetchCatalog();
    }, PULSE_CHECK_MS);

    const scheduleRefetch = () => {
      if (debounceTimer) {
        debounceAgain = true; // cauda: adia, não descarta
        return;
      }
      debounceTimer = setTimeout(function tick() {
        refetchCatalog();
        if (debounceAgain) {
          debounceAgain = false;
          debounceTimer = setTimeout(tick, DEBOUNCE_MS);
        } else {
          debounceTimer = null;
        }
      }, DEBOUNCE_MS);
    };

    const clearTimers = () => {
      if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
      if (offlineTimer) { clearInterval(offlineTimer); offlineTimer = null; }
    };

    const startOfflineBackstop = () => {
      if (offlineTimer || stopped) return;
      offlineTimer = setInterval(refetchCatalog, OFFLINE_BACKSTOP_MS);
    };

    const scheduleReconnect = () => {
      if (stopped || reconnectTimer) return;
      startOfflineBackstop();
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        reconnectMs = Math.min(reconnectMs * 2, RECONNECT_MAX_MS);
        connect();
      }, reconnectMs);
    };

    const connect = () => {
      if (stopped) return;
      const proto = window.location.protocol === "https:" ? "wss" : "ws";
      const url = `${proto}://${window.location.host}/api/catalog/ws?slug=${encodeURIComponent(slug)}`;
      try {
        ws = new WebSocket(url);
      } catch {
        scheduleReconnect();
        return;
      }

      ws.onopen = () => {
        reconnectMs = RECONNECT_MIN_MS;
        if (offlineTimer) { clearInterval(offlineTimer); offlineTimer = null; }
        // Revalida ao (re)conectar: cobre o que mudou enquanto estava fora.
        refetchCatalog();
        if (pingTimer) clearInterval(pingTimer);
        pingTimer = setInterval(() => {
          try { ws?.send("ping"); } catch { /* noop */ }
        }, PING_MS);
      };

      ws.onmessage = (ev) => {
        let type: string | undefined;
        try {
          type = JSON.parse(ev.data as string)?.type;
        } catch {
          return;
        }
        if (type === "catalog_changed") scheduleRefetch();
      };

      ws.onclose = () => {
        clearTimers();
        scheduleReconnect();
      };
      ws.onerror = () => {
        try { ws?.close(); } catch { /* noop */ }
      };
    };

    connect();

    return () => {
      stopped = true;
      clearInterval(pulseTimer);
      clearTimers();
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (debounceTimer) clearTimeout(debounceTimer);
      try { ws?.close(); } catch { /* noop */ }
    };
  }, [slug, queryClient]);
}
